import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { DeleteModal } from '../../../Common/DeleteModal';
import { fetchCategories, addCategory, editCategory, deleteCategory } from '../../../api/blog/categoryService';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import EditCategoryModal from './EditCategoryModal';
import { ReactComponent as EditIcon } from '../../../assets/images/Edit.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/images/Delete.svg';
import { getImageUrl } from '../../../utils/imageUrl';

const PAGE_SIZE_OPTIONS = [10, 15, 20, 50];

const CategoriesTable = forwardRef((props, ref) => {
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editInitialData, setEditInitialData] = useState<any>(null);
  const [originalEditData, setOriginalEditData] = useState<any>(null);
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    fetchCategoriesList();
  }, [page, pageSize]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (search.trim()) {
        setPage(1);
        fetchCategoriesList();
      } else {
        fetchCategoriesList();
      }
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [search]);

  // Dummy data fallback
  const DUMMY_CATEGORIES = [
    {
      _id: '1',
      name: 'Technology',
      icon: 'https://cdn-icons-png.flaticon.com/512/2721/2721297.png',
      description: 'All about the latest in tech.'
    },
    {
      _id: '2',
      name: 'Health',
      icon: 'https://cdn-icons-png.flaticon.com/512/2921/2921822.png',
      description: 'Tips and news on health and wellness.'
    },
    {
      _id: '3',
      name: 'Travel',
      icon: 'https://cdn-icons-png.flaticon.com/512/201/201623.png',
      description: 'Travel stories and guides.'
    }
  ];

  const fetchCategoriesList = async () => {
    try {
      setLoading(true);
      const data = await fetchCategories({ page, pageSize, search });
      let arr: any[] = [];
      if (data && typeof data === 'object' && 'data' in data && Array.isArray((data as any).data?.paginatedResults)) {
        arr = (data as any).data.paginatedResults;
        setTotalPages((data as any).data.totalPages || 1);
        setTotalCount((data as any).data.totalCount || 0);
      } else if (data && typeof data === 'object' && Array.isArray((data as any).paginatedResults)) {
        arr = (data as any).paginatedResults;
        setTotalPages((data as any).totalPages || 1);
        setTotalCount((data as any).totalCount || 0);
      } else if (Array.isArray(data)) {
        arr = data;
        setTotalPages(Math.ceil(arr.length / pageSize) || 1);
        setTotalCount(arr.length);
      }
      setCategories(arr);
    } catch (error) {
      setCategories([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category: any) => {
    const initialData = {
      name: category.category || '',
      icon: category.icon || null,
      description: category.description || '',
      _id: category._id
    };
    
    // Store both initial data for the form and original data for comparison
    setEditInitialData(initialData);
    setOriginalEditData({
      category: category.category || '',
      icon: category.icon || '',
      description: category.description || '',
      isActive: true
    });
    setIsEdit(true);
    setShowEditModal(true);
  };

  const handleAdd = () => {
    setEditInitialData({
      name: '',
      icon: null,
      description: '',
      _id: undefined
    });
    setOriginalEditData(null); // Clear original data for new category
    setIsEdit(false);
    setShowEditModal(true);
  };

  const handleSave = async (data: { name: string; icon: string | null; description: string; _id?: string }) => {
    try {
      console.log('handleSave called with:', { isEdit, data, originalEditData });
      
      if (isEdit && data._id) {
        // For edit, only send fields that have changed
        const currentData = {
          category: data.name,
          description: data.description,
          icon: data.icon || '',
          isActive: true
        };

        // Compare with original data and only include changed fields
        const changedFields: any = {};
        
        // Handle null/undefined values properly
        const originalCategory = originalEditData.category || '';
        const currentCategory = currentData.category || '';
        const originalDescription = originalEditData.description || '';
        const currentDescription = currentData.description || '';
        const originalIcon = originalEditData.icon || '';
        const currentIcon = currentData.icon || '';
        
        if (currentCategory !== originalCategory) {
          changedFields.category = currentData.category;
          console.log(`Category changed: "${originalCategory}" -> "${currentData.category}"`);
        }
        
        if (currentDescription !== originalDescription) {
          changedFields.description = currentData.description;
          console.log(`Description changed: "${originalDescription}" -> "${currentData.description}"`);
        }
        
        if (currentIcon !== originalIcon) {
          changedFields.icon = currentData.icon;
          console.log(`Icon changed: "${originalIcon}" -> "${currentData.icon}"`);
        }
        
        // Always include isActive for consistency
        changedFields.isActive = currentData.isActive;

        console.log('Original data:', originalEditData);
        console.log('Current data:', currentData);
        console.log('Changed fields only:', changedFields);

        // Check if any fields actually changed
        const hasChanges = Object.keys(changedFields).length > 1 || // More than just isActive
                          (Object.keys(changedFields).length === 1 && !changedFields.hasOwnProperty('isActive'));

        if (!hasChanges) {
          console.log('No changes detected, skipping update');
          setShowEditModal(false);
          setOriginalEditData(null);
          return;
        }

        console.log('Calling editCategory with:', { id: data._id, data: changedFields });
        await editCategory(data._id, changedFields);
      } else {
        // For new category, send all required fields
        const apiData = {
          category: data.name,
          description: data.description,
          icon: data.icon || '',
          isActive: true
        };

        console.log('Sending API data for new category:', apiData);
        console.log('Calling addCategory with:', apiData);
        await addCategory(apiData);
      }
      setShowEditModal(false);
      setOriginalEditData(null); // Clear original data after save
      fetchCategoriesList();
    } catch (error) {
      console.error('Error saving category:', error);
      // Log more details about the error
      if (error && typeof error === 'object') {
        console.error('Error details:', JSON.stringify(error, null, 2));
      }
    }
  };

  const handleDelete = (category: any) => {
    setDeleteTarget(category);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (deleteTarget) {
      try {
        await deleteCategory(deleteTarget._id);
        setShowDeleteModal(false);
        setDeleteTarget(null);
        fetchCategoriesList();
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setOriginalEditData(null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleClearSearch = () => {
    setSearch('');
    searchInputRef.current?.focus();
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  useImperativeHandle(ref, () => ({
    openAddModal: handleAdd
  }));

  if (loading) {
    return (
      <div className="custom-table-responsive" style={{ paddingBottom: 32 }}>
        <div className="filter-row">
          <div className="filter-controls">
            <div style={{ width: '240px', height: '38px' }} className="shimmer-base" />
          </div>
        </div>
        <ShimmerLoader type="table" rows={5} />
      </div>
    );
  }

  return (
    <div className="custom-table-responsive" style={{ paddingBottom: 32 }}>
      <div className="filter-row" style={{ marginBottom: 0 }}>
        <div className="filter-controls">
          <div style={{ position: 'relative', width: 240 }}>
            <input
              type="text"
              className="form-search"
              placeholder="Search by name or description"
              value={search}
              onChange={handleSearchChange}
              ref={searchInputRef}
              style={{ width: 240, paddingRight: 32 }}
            />
            {search && (
              <button
                type="button"
                onClick={handleClearSearch}
                style={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  zIndex: 2
                }}
                aria-label="Clear search"
              >
                <span style={{ fontSize: 18, color: '#888' }}>&times;</span>
              </button>
            )}
          </div>
        </div>
      </div>
      <table className="custom-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Icon</th>
            <th>Description</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {categories.length === 0 ? (
            <tr><td colSpan={5} style={{textAlign:'center'}}>No categories found.</td></tr>
          ) : (
            categories.map((category, idx) => (
              <tr key={category._id || idx}>
                <td>{(page - 1) * pageSize + idx + 1}</td>
                <td>{category.category}</td>
                <td>{category.icon ? <img src={getImageUrl(category.icon)} alt="icon" style={{ width: 32, height: 32 }} /> : '-'}</td>
                <td>{category.description || '-'}</td>
                <td>
                  <div className="d-flex gap-2">
                    <button
                      className="icon-btn edit-btn"
                      onClick={() => handleEdit(category)}
                      title="Edit"
                      type="button"
                    >
                      <EditIcon />
                    </button>
                    <button
                      className="icon-btn delete-btn"
                      onClick={() => handleDelete(category)}
                      title="Delete"
                      type="button"
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {/* Pagination UI */}
      {totalPages > 1 && (
        <div
          className="d-flex pagination-row align-items-center mt-3"
          style={{
            width: 1160,
            height: 52,
            justifyContent: 'space-between',
            borderRadius: '8px',
            paddingTop: 8,
            paddingRight: 12,
            paddingBottom: 8,
            paddingLeft: 12,
          }}
        >
          <div
            style={{
              fontFamily: 'Helvetica Neue, Arial, sans-serif',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '16px',
              letterSpacing: 0,
            }}
          >
            Showing results
            <select
              value={pageSize}
              onChange={handlePageSizeChange}
              className="mx-2"
              style={{
                width: 70,
                height: 36,
                borderRadius: 8,
                padding: 8,
                gap: 8,
                borderWidth: 0.5,
                borderStyle: 'solid',
                borderColor: '#888',
                fontFamily: 'Helvetica Neue, Arial, sans-serif',
                fontSize: '12px',
              }}
            >
              {PAGE_SIZE_OPTIONS.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            of {totalCount}
          </div>
          <div className="d-flex align-items-center">
            Page
            <button className="btn btn-light mx-1" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>&lt;</button>
            <span className="btn btn-primary mx-1 disabled">{page}</span>
            {Array.from({ length: Math.min(3, totalPages) }, (_, i) => i + 1 + page).map(p => (
              p <= totalPages ? (
                <button key={p} className="btn btn-light mx-1" onClick={() => handlePageChange(p)}>{p}</button>
              ) : null
            ))}
            {page < totalPages - 2 && <span className="mx-1">...</span>}
            <button className="btn btn-light mx-1" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>&gt;</button>
            <span className="mx-2">of {totalPages}</span>
          </div>
        </div>
      )}
      <DeleteModal
        show={showDeleteModal}
        handleClose={closeDeleteModal}
        deleteModalFunction={confirmDelete}
        title="Delete Category"
        message={`Are you sure you want to delete category '${deleteTarget?.name}'?`}
      />
      <EditCategoryModal
        show={showEditModal}
        handleClose={closeEditModal}
        onSave={handleSave}
        initialData={editInitialData}
      />
    </div>
  );
});

export default CategoriesTable; 